import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { isNativeApp } from '../utils/platform';

/**
 * "MeMo can only see your main calendar."
 *
 * A tester wrote that she could see one event of hers and no more, and the
 * server log said why, over and over: `could not list calendars: Insufficient
 * Permission`. Her Google account never granted MeMo the right to read the
 * LIST of calendars, so MeMo read the main one and showed what it found —
 * confidently, completely, and wrong. Everything on any other calendar simply
 * did not exist as far as the app was concerned, and nothing on the screen
 * gave her a reason to doubt it.
 *
 * That is the failure this whole app keeps having to answer for: not being
 * unable to do something, but being unable to SAY that it could not. A
 * calendar showing two thirds of your week without a word is worse than one
 * that admits what it cannot see — because the first one you trust.
 *
 * Reconnecting re-runs Google's consent and picks the permission up, so the
 * message carries the button that does it rather than describing it.
 */
const L = {
  it: { t: 'MeMo vede solo il tuo calendario principale.', d: 'Gli eventi che stanno su altri calendari del tuo account non compaiono. Ricollega Google per vederli tutti.', b: 'Ricollega Google', x: 'Non ora' },
  en: { t: 'MeMo can only see your main calendar.', d: 'Events on your account’s other calendars are not showing. Reconnect Google to see them all.', b: 'Reconnect Google', x: 'Not now' },
  es: { t: 'MeMo solo ve tu calendario principal.', d: 'Los eventos de los demás calendarios de tu cuenta no aparecen. Reconecta Google para verlos todos.', b: 'Reconectar Google', x: 'Ahora no' },
  de: { t: 'MeMo sieht nur deinen Hauptkalender.', d: 'Termine in den anderen Kalendern deines Kontos fehlen. Verbinde Google neu, um alle zu sehen.', b: 'Google neu verbinden', x: 'Nicht jetzt' },
  fr: { t: 'MeMo ne voit que votre agenda principal.', d: 'Les événements des autres agendas de votre compte n’apparaissent pas. Reconnectez Google pour tous les voir.', b: 'Reconnecter Google', x: 'Pas maintenant' },
  nl: { t: 'MeMo ziet alleen je hoofdagenda.', d: 'Afspraken in je andere agenda’s verschijnen niet. Koppel Google opnieuw om alles te zien.', b: 'Google opnieuw koppelen', x: 'Niet nu' },
  pt: { t: 'O MeMo só vê o teu calendário principal.', d: 'Os eventos nos outros calendários da tua conta não aparecem. Liga o Google de novo para os veres todos.', b: 'Ligar o Google de novo', x: 'Agora não' },
  ru: { t: 'MeMo видит только основной календарь.', d: 'События из других календарей вашего аккаунта не показываются. Подключите Google заново, чтобы увидеть всё.', b: 'Подключить Google заново', x: 'Не сейчас' },
  pl: { t: 'MeMo widzi tylko Twój główny kalendarz.', d: 'Wydarzenia z pozostałych kalendarzy konta nie są pokazywane. Połącz Google ponownie, aby zobaczyć wszystko.', b: 'Połącz Google ponownie', x: 'Nie teraz' },
  ro: { t: 'MeMo vede doar calendarul tău principal.', d: 'Evenimentele din celelalte calendare ale contului nu apar. Reconectează Google pentru a le vedea pe toate.', b: 'Reconectează Google', x: 'Nu acum' },
  zh: { t: 'MeMo 只能看到你的主日历。', d: '你账户中其他日历的日程不会显示。重新连接 Google 即可看到全部。', b: '重新连接 Google', x: '暂不' },
  ja: { t: 'MeMo はメインのカレンダーしか見えていません。', d: 'アカウント内の他のカレンダーの予定は表示されません。Google を再接続するとすべて表示されます。', b: 'Google を再接続', x: '後で' },
  hi: { t: 'MeMo केवल आपका मुख्य कैलेंडर देख पा रहा है।', d: 'आपके खाते के अन्य कैलेंडर के इवेंट नहीं दिख रहे। सब देखने के लिए Google दोबारा जोड़ें।', b: 'Google दोबारा जोड़ें', x: 'अभी नहीं' },
  ko: { t: 'MeMo가 기본 캘린더만 볼 수 있습니다.', d: '계정의 다른 캘린더에 있는 일정은 표시되지 않습니다. Google을 다시 연결하면 모두 보입니다.', b: 'Google 다시 연결', x: '나중에' },
  ar: { t: 'يرى MeMo تقويمك الرئيسي فقط.', d: 'الأحداث الموجودة في تقاويم حسابك الأخرى لا تظهر. أعد ربط Google لرؤيتها كلها.', b: 'إعادة ربط Google', x: 'ليس الآن' },
};

// Put away for a day, not for ever: the events are still missing tomorrow.
const KEY = 'memo_calendars_limited_hidden';

export default function CalendarsLimited() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(KEY) === new Date().toLocaleDateString('en-CA'); }
    catch (_) { return false; }
  });

  if (!user?.calendarsLimited || hidden) return null;

  const base = String(user.language || 'it').split('-')[0];
  const t = L[base] || L.en;

  function reconnect() {
    const api = process.env.REACT_APP_API_URL || '';
    // The installed app has to say so, exactly as the landing page does, or it
    // comes back from Google into a browser instead of into MeMo.
    window.location.href = `${api}/auth/google${isNativeApp() ? '?native=1' : ''}`;
  }

  function putAway() {
    try { localStorage.setItem(KEY, new Date().toLocaleDateString('en-CA')); } catch (_) {}
    setHidden(true);
  }

  return (
    <div role="status" style={{
      margin: '0 0 12px', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
      border: '1.5px solid #e6c200', background: '#fffbe6',
      fontSize: '0.8rem', lineHeight: 1.45, color: '#6b5800',
    }}>
      <div style={{ fontWeight: 800, marginBottom: 2 }}>{t.t}</div>
      <div style={{ marginBottom: 8 }}>{t.d}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={reconnect} style={{
          background: 'var(--color-primary)', color: '#fff', border: 'none',
          borderRadius: 8, padding: '7px 12px', fontSize: '0.8rem', fontWeight: 700,
          fontFamily: 'inherit', cursor: 'pointer',
        }}>{t.b}</button>
        <button type="button" onClick={putAway} style={{
          background: 'none', border: 'none', color: '#6b5800',
          fontSize: '0.8rem', fontFamily: 'inherit', cursor: 'pointer',
          textDecoration: 'underline', padding: '7px 4px',
        }}>{t.x}</button>
      </div>
    </div>
  );
}
